import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import CalculadoraViagem from '../CalculadoraViagem';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'calculadora-viagem';

/** Página /ferramentas/calculadora-viagem */
const ViagemPage: React.FC = () => (
  <>
    <Helmet>
      <title>Calculadora de Viagem e Racha Conta: Quanto Vai Custar? – Gas2</title>
      <meta
        name="description"
        content="Calcule a distância exata, o custo com combustível e pedágios, e descubra o valor do PIX que cada passageiro deve mandar. Grátis, sem cadastro."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:title" content="Calculadora de Viagem e Racha Conta – Gas2" />
      <meta
        property="og:description"
        content="Calcule distância, custo de combustível, pedágios e o valor do PIX por passageiro para qualquer viagem de carro no Brasil."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Calculadora de Viagem e Racha Conta – Gas2" />
      <meta
        name="twitter:description"
        content="Descubra a distância, o gasto com gasolina e pedágios, e quanto cada um deve pagar na viagem."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Racha Conta de Viagem
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Calculadora de Viagem: Quanto Vai Custar e Quanto Cada Um Paga?
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Digite a origem e o destino — a calculadora busca a distância exata via
            satélite — e então informe o consumo do carro, o preço do combustível e a
            estimativa de pedágios. Em segundos você terá o custo total da viagem (ida e
            volta) e o valor exato do PIX para cada passageiro.
          </p>
        </motion.header>

        {/* ── Calculadora ── */}
        <CalculadoraViagem />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Como Calcular o Custo Real de uma Viagem de Carro?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              O cálculo vai além de "preço do litro × litros no tanque". Você precisa
              considerar a{' '}
              <strong className="text-slate-200">
                distância total percorrida (ida e volta)
              </strong>
              , o consumo médio real do carro na estrada (que costuma ser melhor do que na
              cidade), o custo de pedágios e, se a viagem incluir vários passageiros, a
              divisão proporcional de cada gasto. Nossa calculadora faz tudo isso
              automaticamente.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              A distância é traçada pela API pública do{' '}
              <strong className="text-slate-200">OpenStreetMap + OSRM</strong>
              , o mesmo motor de roteamento usado por aplicativos de GPS, garantindo que
              o trajeto calculado reflita o caminho real de carro — e não a distância em
              linha reta.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Vale Mais a Pena Ir de Carro ou de Ônibus/Avião?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Depende do número de passageiros. Com 4 pessoas, o custo por pessoa numa
              viagem de carro tende a ser até 60% menor do que passagens individuais de
              ônibus convencional, e até 80% mais barato que voos domésticos. Mas com 1
              pessoa, o carro raramente é a opção mais econômica quando se somam pedágios
              e desgaste do veículo.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              Use o campo <strong className="text-slate-200">"Pessoas rateando"</strong>{' '}
              para visualizar como o preço por passageiro cai à medida que o carro fica
              mais cheio. Esse é um dos fatores mais decisivos para tornar a viagem de
              carro uma vantagem real.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Dicas para Reduzir o Custo na Estrada
            </h2>
            <ul className="mt-3 space-y-2 text-slate-400 list-disc list-inside leading-relaxed">
              <li>
                Calibre os pneus com 2–4 PSI acima do recomendado para cidade — a
                resistência ao rolamento é menor em piso liso.
              </li>
              <li>
                Mantenha velocidade constante entre 90–110 km/h; acima de 120 km/h o
                arrasto aerodinâmico cresce exponencialmente.
              </li>
              <li>
                Evite peso desnecessário no porta-malas — cada 40 kg extras consomem
                cerca de 3% a mais de combustível.
              </li>
              <li>
                Use o freio motor descendo serras em vez de pisar no freio, e aproveite
                a injeção cortada para economizar.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Perguntas Frequentes sobre Custo de Viagem
            </h2>
            <div className="space-y-4 mt-3">
              {[
                {
                  q: 'O consumo na estrada é diferente do consumo na cidade?',
                  a: 'Sim. Em rodovias, sem paradas e com velocidade estável, a maioria dos carros apresenta consumo 20–30% melhor do que em tráfego urbano. Se você usa o Gas2, confira a média registrada em abastecimentos após viagens longas.',
                },
                {
                  q: 'Como incluir pedágio no cálculo?',
                  a: 'Informe a soma estimada de todos os pedágios do trajeto (apenas ida). A calculadora multiplica pelo número de trechos (1 ou 2, se ida e volta) e já divide entre os passageiros.',
                },
                {
                  q: 'Posso calcular viagens para fora do Brasil?',
                  a: 'A API do OpenStreetMap cobre o mundo inteiro. Basta informar as cidades de origem e destino por extenso (ex: "Buenos Aires, Argentina") que a rota será traçada normalmente.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <p className="font-semibold text-white text-sm">{q}</p>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default ViagemPage;


